/**
 *
 *  CodeVerification for PasswordReset
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 13/06/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState, useEffect, useCallback, useRef, MouseEvent, KeyboardEvent, ChangeEvent } from 'react';
import { ScrollView, View } from 'react-native';
import { useAppState } from '../../../state';
import { useSignUpState } from '../../../state/SignUpState';
import clsx from 'clsx';
import * as Sentry from '@sentry/browser';

import { styled, Theme } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Lottie from 'react-lottie';

// Components

// Icons
import ArrowRightIcon from '../../../icons/ArrowRightIcon';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

import animationData from '../../../lotties/loading-spinner.json';

import { makeStyles } from '@material-ui/core/styles';

const MAX_INPUTS = 6;
const TIME_TO_WAIT_BEFORE_SEND_AGAIN = 60;

// ========== LOADER ==========
export const LoaderContainer = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  'z-index': 1000,
});

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

type CountryCode = 'US' | 'UK' | 'CA' | 'DE' | 'UNKNOWN';

function detectCountryByCode(countryCode: string): CountryCode {
  const code = countryCode?.replace(/\D/g, '');
  if (code === '1') return 'US'; // US и Canada используют один формат
  if (code === '44') return 'UK';
  if (code === '49') return 'DE';
  return 'UNKNOWN';
}

// type Partner = {
//   partnerRatingRecap: {
//     average: number;
//     numberOfRatings: number;
//   }
// };

interface CodeInputProps {
  className?: string;
  style?: any;
  index: number;
  hasError: boolean;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>, index: number) => void;
  onInput: (event: KeyboardEvent<HTMLInputElement>, index: number) => void;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
}

interface SendCodeAgainButtonProps {
  onClick: () => void;
}

interface PartnerRatingProps {
  // partner: Partner;
  className: string;
  trainerRatingRecapClassName: string;
  trainerNumberOfSessionsClassName: string;
  onClick: (event: MouseEvent<HTMLElement>) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  codeInputsContainer: {
    position: 'relative',
    // 'margin-bottom': '1em',
    '&:before': {
      content: "'-'",
      position: 'absolute',
      top: '17px',
      left: '50%',
      transform: 'translateX(-6px)',
      'font-family': 'Poppins',
      'font-weight': '600',
      'font-size': '18px',
      'line-height': '26px',
      color: 'rgba(48, 55, 64, 1)',
    },
  },
  codeInput: {
    // font-family: Poppins,sans-serif;
    'font-size': '22px',
    'font-weight': '400',
    'line-height': '1.5',
    padding: '16px',
    // 'padding-left': '56px',
    // 'padding-right': '8px',
    cursor: 'pointer',
    'min-width': '0', //'51px',
    height: '64px',
    'margin-bottom': '0',
    // 'border-color': 'rgb(224 234 243)',
    'border-radius': '0.5rem',
    'border-style': 'solid',
    'border-width': '1px',
    // 'border-color': 'rgba(203, 207, 213, 1)', // ✅ дефолтный цвет обводки (tbk-grey-inactive)
    'border-color': theme.greyInactive || 'rgba(203, 207, 213, 1)',

    // padding: '11px 40px 11px 16px',
    display: 'inline-block',
    'vertical-align': 'text-bottom',
    'text-align': 'center',

    // Плавный переход цвета обводки
    // transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    transition: 'border-color 0.2s ease',
    outline: 'none',

    // ✅ При наведении
    '&:hover': {
      'border-color': 'rgba(150, 155, 163, 1)', // чуть темнее дефолтного
    },

    // ✅ При фокусе
    '&:focus': {
      // 'border-color': 'rgba(107, 113, 120, 1)', // tbk-grey
      // 'border-color': theme.palette.tbkGrey || 'rgba(107, 113, 120, 1)',
      'border-color': theme.grey || 'rgba(107, 113, 120, 1)',

      // 'box-shadow': '0 0 0 3px rgba(107, 113, 120, 0.15)', // лёгкая подсветка
    },

    // ✅ При ошибке (если передаётся hasError)
    '&[data-error="true"], &.has-error': {
      'border-color': '#ef4444',
      '&:focus': {
        'border-color': '#ef4444',
        // 'box-shadow': '0 0 0 3px rgba(239, 68, 68, 0.15)',
      },
    },
  },
  errorMessage: {
    color: 'red',
    display: 'flex',
    'align-items': 'center',
    // margin: '-8px 0 24px',
    marginTop: '8px',
    '& svg': {
      'margin-right': '0.4em',
    },
  },
  submitButton: {
    // 'margin-right': '24px',
    width: '100%',
  },
  sendAgainButton: {
    // color: theme.blackGrey, // #E94E32
    // #72879A
    color: theme.blueGrey,
    // 'background-color': 'transparent',
    cursor: 'pointer',
    // display: 'flex',
    background: 'transparent',
    border: '0',
    padding: '0.4em',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '18px',
  },
}));

// {`${className} tbk-input-field tbk-text-title-medium tbk-mb-auto tbk-mr-1 tbk-pl-1 tbk-pr-1 tbk-text-center ${
//         hasError ? "tbk-border-coral" : "tbk-border-grey-inactive"
//       }`}

const CodeInput = ({ className, style, index, hasError, onKeyDown, onInput, disabled }: CodeInputProps) => {
  const classes = useStyles();

  const handleKeydown = useCallback(
    e => {
      onKeyDown && onKeyDown(e, index);
    },
    [index, onKeyDown]
  );

  const handleChange = useCallback(
    e => {
      onInput && onInput(e, index);
    },
    [index, onInput]
  );

  return (
    <input
      type="tel"
      className={clsx(classes.codeInput, className)}
      // hasError ? "tbk-border-coral" : "tbk-border-grey-inactive"
      style={style}
      onKeyDown={handleKeydown}
      onInput={handleChange}
      disabled={disabled}
    />
  );
};

const SendCodeAgainButton = ({ onClick }: SendCodeAgainButtonProps) => {
  const classes = useStyles();

  const [sendCodeAgainTime, setSendCodeAgainTime] = useState(TIME_TO_WAIT_BEFORE_SEND_AGAIN);

  const intervalRef = useRef(null);
  const sendCodeAgainTimeRef = useRef(0);

  const startTimer = () => {
    sendCodeAgainTimeRef.current = TIME_TO_WAIT_BEFORE_SEND_AGAIN;

    // @ts-ignore
    intervalRef.current = setInterval(() => {
      sendCodeAgainTimeRef.current--;

      if (sendCodeAgainTimeRef.current < 0) {
        // @ts-ignore
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      setSendCodeAgainTime(sendCodeAgainTimeRef.current);
    }, 1000);
  };

  useEffect(() => {
    startTimer();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const handleClick = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    } else {
      startTimer();
    }

    onClick && onClick();
  }, [onClick]);

  return (
    <button
      className={classes.sendAgainButton}
      // className={classes.sendAgainButton `tbk-text-small tbk-text-grey hover:tbk-text-grey-inactive ${
      //   intervalRef.current !== null ? 'tbk-cursor-disabled' : 'tbk-underline'
      // }`}
      disabled={intervalRef.current !== null}
      onClick={handleClick}
    >
      Send code again {intervalRef.current !== null ? `in ${sendCodeAgainTime}s` : ''}
    </button>
  );
};

export default function CodeVerification() {
  const classes = useStyles();

  const [canSend, setCanSend] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [formattedPhone, setFormattedPhone] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState<boolean>(false); // ✅ НОВОЕ: флаг верификации

  const { sendResetCode, verifyPhoneNumberPasswordReset, error, isFetching } = useAppState();
  const { setAuthState, setResetCode, userPhoneCode, userPhoneNumber } = useSignUpState();

  const codeInputsContainerRef = useRef<HTMLDivElement>(null);

  // ========== АВТОФОРМАТИРОВАНИЕ НОМЕРА ==========
  useEffect(() => {
    if (userPhoneNumber && userPhoneCode) {
      const formatted = formatPhoneForDisplay(userPhoneNumber, userPhoneCode);
      setFormattedPhone(formatted);
    } else if (userPhoneNumber) {
      setFormattedPhone(userPhoneNumber);
    }
  }, [userPhoneNumber, userPhoneCode]);

  // ========== АВТОВЕРИФИКАЦИЯ ПОСЛЕ ВВОДА ПОСЛЕДНЕЙ ЦИФРЫ ==========
  useEffect(() => {
    if (canSend && !isVerifying) {
      const code = getCode();
      if (code && code.length === 6) {
        setIsVerifying(true);

        verifyPhoneNumberPasswordReset(userPhoneCode, userPhoneNumber, code)
          .then(() => {
            setResetCode(code);
            setAuthState('passwordReset');
          })
          .catch(err => {
            console.error('[CodeVerification] Verification failed:', err);
            setHasError(true);
            setIsVerifying(false);
            setCanSend(false); // ✅ Сбрасываем, чтобы можно было попробовать снова
          });
      }
    }
  }, [canSend, isVerifying, userPhoneCode, userPhoneNumber]);

  const triggerInputCodeChangeEvent = (ele: HTMLInputElement, value: any) => {
    let lastValue = 'pls help me trigger this event';

    if (!ele) {
      return;
    }

    ele.value = value;

    let ev = new Event('input', { bubbles: true });

    // @ts-ignore
    ev.simulated = true;

    // @ts-ignore
    let tracker = ele._valueTracker;

    if (tracker) {
      tracker.setValue(lastValue);
    }

    ele.dispatchEvent(ev);
  };

  const handleCodeInputKeydown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    // @ts-ignore
    if (e.keyCode === 8 && e.target.value === '' && index > 0) {
      (codeInputsContainerRef?.current?.childNodes[index - 1] as HTMLInputElement).focus();
    }
  };

  const handleCodeInputChange = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    const regExpr = new RegExp(/[^0-9]/g);

    // @ts-ignore
    if (regExpr.test(e.target.value)) {
      // @ts-ignore
      e.target.value = e.target.value.replace(regExpr, '');
    }

    // @ts-ignore
    const [first, ...rest] = e.target.value;

    // @ts-ignore
    e.target.value = first ?? ''; // first will be undefined when backspace was entered, so set the input to ""

    // @ts-ignore
    if (!index && e.inputIndex) {
      // @ts-ignore
      index = e.inputIndex;
    }

    const lastInputBox = index === MAX_INPUTS - 1;
    const didInsertContent = first !== undefined;

    if (didInsertContent && !lastInputBox) {
      let codeInput = codeInputsContainerRef?.current?.childNodes[index + 1] as HTMLInputElement;
      codeInput?.focus();
      triggerInputCodeChangeEvent(codeInput, rest.join(''));
    }

    // ✅ Проверяем валидность кода после каждого изменения
    const isValid = isCodeValid();
    setCanSend(isValid);
    setHasError(false);
  };

  const getCode = () =>
    codeInputsContainerRef.current == null
      ? ''
      : [].map.call(codeInputsContainerRef.current.childNodes, ({ value }) => value).join('');

  // const isCodeValid = () => {
  //   let code = getCode();
  //   return code && code.length === 6;
  // };

  const isCodeValid = () => {
    let code = getCode();
    return !!(code && code.length === 6); // ✅ Явное приведение к boolean
  };

  const sendCodeAgain = useCallback(() => {
    if (userPhoneCode && userPhoneNumber) {
      sendResetCode(userPhoneCode, userPhoneNumber)
        .then(() => {
          console.log('[CodeVerification] Code resent successfully');
        })
        .catch(err => {
          console.error('[CodeVerification] Failed to resend code:', err);
          Sentry.captureException(err);
        });
    }
  }, [userPhoneCode, userPhoneNumber, sendResetCode]);

  // Автофокус на первый инпут
  useEffect(() => {
    if (codeInputsContainerRef.current && codeInputsContainerRef.current.childNodes[0]) {
      (codeInputsContainerRef.current.childNodes[0] as HTMLInputElement).focus();
    }
  }, []);

  return (
    <>
      <div
        // display="flex"
        // alignItems="center"
        // justifyContent="center"
        // flexDirection="column"
        // textAlign="center"
        // flexGrow={1}
        // // mb={5}
        style={{
          display: 'flex',
          alignItems: 'start',
          justifyContent: 'center',
          flexDirection: 'column',
          // textAlign: 'center',
          flexGrow: 1,
          position: 'relative', // ✅ нужно для абсолютного позиционирования спиннера
        }}
      >
        {/* ========== LOADING OVERLAY ========== */}
        {isVerifying && (
          <>
            <LoaderContainer>
              <Lottie options={defaultOptions} height={128} width={128} />
            </LoaderContainer>
            <View
              style={{
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                bottom: '0',
                backgroundColor: 'rgb(255, 255, 255)',
                opacity: 0.7,
                zIndex: 999,
                // pointerEvents: 'all', // клики по оверлею блокируют фон
              }}
            />
          </>
        )}

        <Typography
          variant="h5"
          // className={classes.gutterBottom}
          style={{
            fontWeight: 600,
            marginBottom: '8px',
          }}
        >
          6 Digit Code
        </Typography>
        {/*<div className="tbk-text-title-bold tbk-mb-1 tbk-text-grey-main">

  </div>*/}
        <Typography
          variant="subtitle2"
          // className={classes.gutterBottom}
          style={{
            marginBottom: '32px',
          }}
        >
          {/*<p className="tbk-text-main tbk-mb-4 tbk-text-grey-main">*/}
          {/* We've sent you a code on {userPhoneCode} {userPhoneNumber} to reset your current password. */}
          {/*</p>*/}
          We sent a verification code to {formattedPhone || userPhoneNumber}
        </Typography>

        <div
          style={{
            width: '100%',
            // marginBottom: '32px',
            marginBottom: '28px',
          }}
        >
          <Grid
            ref={codeInputsContainerRef}
            container
            justifyContent="space-between"
            wrap={'nowrap'}
            className={classes.codeInputsContainer}
          >
            {[...new Array(MAX_INPUTS)].map((v, index) => (
              <CodeInput
                key={index}
                // className={index === 2 ? 'tbk-mr-[27px]' : ''}
                style={{
                  marginRight: index === 2 ? '18px' : index !== 5 ? '4px' : '',
                }}
                index={index}
                hasError={hasError}
                onKeyDown={handleCodeInputKeydown}
                onInput={handleCodeInputChange}
                disabled={isVerifying}
              />
            ))}
          </Grid>

          {hasError ? (
            <Typography variant="caption" className={classes.errorMessage}>
              <ErrorOutlineIcon />
              {error ? error.message : 'Unknown error has occured. Please, try again later.'}
            </Typography>
          ) : null}

          {/* {isVerifying && (
            <Typography
              variant="body2"
              style={{
                textAlign: 'center',
                marginTop: '16px',
                // color: theme => theme.palette.text.secondary,
              }}
            >
              Verifying...
            </Typography>
          )} */}
        </div>
      </div>

      <SendCodeAgainButton onClick={sendCodeAgain} />
    </>
  );
}

// ========== ФОРМАТИРОВАНИЕ US ==========
function formatUSPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  const number = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (number.length !== 10) return phone;
  return `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
}

// ========== ФОРМАТИРОВАНИЕ UK ==========
function formatUKPhone(phone: string): string {
  let digits = phone.replace(/\D/g, '');

  if (digits.startsWith('44') && digits.length > 10) {
    digits = digits.slice(2);
  }

  if (!digits.startsWith('0')) {
    digits = '0' + digits;
  }

  if (digits.length < 10 || digits.length > 11) return phone;

  if (digits.startsWith('07') && digits.length === 11) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
  }

  if (digits.startsWith('020') && digits.length === 11) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
  }

  if ((digits.startsWith('01') || digits.startsWith('02')) && digits.length === 11) {
    return `${digits.slice(0, 5)} ${digits.slice(5)}`;
  }

  if (digits.startsWith('01') && (digits.length === 10 || digits.length === 11)) {
    return `${digits.slice(0, 4)} ${digits.slice(4)}`;
  }

  if (/^0[389]/.test(digits) && digits.length === 11) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
  }

  return `${digits.slice(0, 4)} ${digits.slice(4)}`;
}

// ========== УНИВЕРСАЛЬНОЕ ФОРМАТИРОВАНИЕ ==========
function formatPhoneByCountry(phone: string, countryCode: string): string {
  const country = detectCountryByCode(countryCode);
  switch (country) {
    case 'US':
      return formatUSPhone(phone);
    case 'UK':
      return formatUKPhone(phone);
    // case 'DE':
    //   return formatDEPhone(phone); // добавь функцию
    default:
      return phone;
  }
}
// ========== ФОРМАТИРОВАНИЕ С КОДОМ СТРАНЫ ДЛЯ ОТОБРАЖЕНИЯ ==========
function formatPhoneForDisplay(phone: string, countryCode: string): string {
  const formatted = formatPhoneByCountry(phone, countryCode);
  const country = detectCountryByCode(countryCode);

  switch (country) {
    case 'US':
      return formatted; // US обычно показывают без +1 в локальном контексте
    case 'UK':
      return formatted;
    default:
      return `+${countryCode?.replace(/\D/g, '')} ${formatted}`;
  }
}
